import { Account, UserType } from '@api';
import {
    AccountBalance,
    AdminPanelSettings,
    AttachMoney,
    Badge,
    Dashboard,
    Description,
    Groups,
    Handshake,
    Inventory,
    LockClock,
    Person,
    PriceCheck,
    StackedLineChart,
    SwitchAccount,
    Topic,
    Tune
} from '@mui/icons-material';
import { Popover } from '@mui/material';
import { useTryGetCurrentAccount } from '@utils/hooks';
import clsx from 'clsx';
import {
    Children,
    ComponentProps,
    Dispatch, ReactElement, ReactNode, SetStateAction, cloneElement, isValidElement, useState
} from 'react';
import { useLocation } from 'react-router-dom';

import logoWhite from '../../../images/logo-white.png';

import styles from './MainNav.module.scss';
import NavItem from './components/NavItem';
import NavList from './components/NavList';


const { CLIENT, CUSTOMER, INTERNAL } = UserType;

interface MainNavProps {
    account: Account | undefined;
}

export default function MainNav({ account }: MainNavProps) {
    const { accountUserType, id: clientId, customerId } = useTryGetCurrentAccount() || {};

    const [ adminAnchorEl, setAdminAnchorEl ] = useState<HTMLElement | null>(null);
    const [ trackingAnchorEl, setTrackingAnchorEl ] = useState<HTMLElement | null>(null);

    const isAdmin = !!useLocation().pathname.match(/\/accounts\/.*\/admin/);
    const isTracking = !!useLocation().pathname.match(/\/accounts\/.*\/tracking/);

    function closeSubmenus() {
        setAdminAnchorEl(null);
        setTrackingAnchorEl(null);
    }

    const adminOpen = !!adminAnchorEl;
    const trackingOpen = !!trackingAnchorEl;
    const trackingOrAdminOpen = adminOpen || trackingOpen;

    const accountUrlPrefix = customerId
        ? `/accounts/${clientId}/${customerId}`
        : `/accounts/${clientId}`;

    if (isTracking) {
        console.log('tracking!');
    }

    const adminNavItems = (
        <>
            {accountUserType === INTERNAL && (
                <NavItem
                    to={`${accountUrlPrefix}/admin/clients`}
                    label="Clients"
                    Icon={Groups}
                    onClick={closeSubmenus}
                    dark={adminOpen}
                />
            )}

            {accountUserType === INTERNAL && (
                <NavItem
                    to={`${accountUrlPrefix}/admin/investors`}
                    label="Investors"
                    Icon={AccountBalance}
                    onClick={closeSubmenus}
                    dark={adminOpen}
                />
            )}

            {accountUserType === CLIENT && (
                <NavItem
                    to={`${accountUrlPrefix}/admin/bulk-commitments`}
                    label="Bulk Commitments"
                    Icon={Handshake}
                    onClick={closeSubmenus}
                    dark={adminOpen}
                />
            )}

            {accountUserType === CLIENT && (
                <NavItem
                    to={`${accountUrlPrefix}/admin/customers`}
                    label="Customers"
                    Icon={Groups}
                    onClick={closeSubmenus}
                    dark={adminOpen}
                />
            )}

            {accountUserType === CLIENT && (
                <NavItem
                    to={`${accountUrlPrefix}/admin/investor-eligibility`}
                    label="Investor Eligibility"
                    Icon={AccountBalance}
                    onClick={closeSubmenus}
                    dark={adminOpen}
                />
            )}

            {accountUserType === CLIENT && (
                <NavItem
                    to={`${accountUrlPrefix}/admin/llpas`}
                    label="LLPAs"
                    Icon={Tune}
                    onClick={closeSubmenus}
                    dark={adminOpen}
                />
            )}

            {accountUserType === CLIENT && (
                <NavItem
                    to={`${accountUrlPrefix}/admin/lock-availability`}
                    label="Lock Availability"
                    Icon={LockClock}
                    onClick={closeSubmenus}
                    dark={adminOpen}
                />
            )}

            {accountUserType === CLIENT && (
                <NavItem
                    to={`${accountUrlPrefix}/admin/products`}
                    label="Products"
                    Icon={Inventory}
                    onClick={closeSubmenus}
                    dark={adminOpen}
                />
            )}

            {accountUserType === CLIENT && (
                <NavItem
                    to={`${accountUrlPrefix}/admin/purchase-advice`}
                    label="Purchase advice"
                    Icon={AttachMoney}
                    onClick={closeSubmenus}
                    dark={adminOpen}
                />
            )}

            {(accountUserType === CLIENT || accountUserType === CUSTOMER) && (
                <NavItem
                    to={`${accountUrlPrefix}/admin/reference-guides`}
                    label="Reference guides"
                    Icon={Topic}
                    onClick={closeSubmenus}
                    dark={adminOpen}
                />
            )}

            {accountUserType !== CUSTOMER && (
                <NavItem
                    to={`${accountUrlPrefix}/admin/roles`}
                    label="Roles"
                    Icon={Badge}
                    onClick={closeSubmenus}
                    dark={adminOpen}
                />
            )}

            <NavItem
                to={`${accountUrlPrefix}/admin/users`}
                label="Users"
                Icon={Person}
                onClick={closeSubmenus}
                dark={adminOpen}
            />
        </>
    );

    const trackingNavItem = (
        <NavItem
            to={`${accountUrlPrefix}/tracking/bulk-commitments`}
            label="Bulk Commitments"
            Icon={Handshake}
            onClick={closeSubmenus}
            dark={trackingOpen}
        />
    );

    const mainNavItems = !account ? (
        <NavItem
            to="/accounts"
            label="Accounts"
            Icon={SwitchAccount}
        />
    ) : (
        <>
            <NavItem
                exact
                to={accountUrlPrefix}
                label="Dashboard"
                Icon={Dashboard}
                onClick={closeSubmenus}
                selected={trackingOrAdminOpen ? false : undefined}
            />

            <NavItem
                to={`${accountUrlPrefix}/loans`}
                label="Loans"
                Icon={Description}
                onClick={closeSubmenus}
                selected={trackingOrAdminOpen ? false : undefined}
            />

            <NavItem
                to={`${accountUrlPrefix}/product-pricing`}
                label="Product & Pricing"
                Icon={PriceCheck}
                onClick={closeSubmenus}
                selected={trackingOrAdminOpen ? false : undefined}
            />

            <NavItem
                selected={trackingOpen || isTracking}
                label="Tracking"
                Icon={StackedLineChart}
                onClick={(event) => {
                    closeSubmenus();
                    setTrackingAnchorEl(event.currentTarget);
                }}
            />

            {isTracking && !trackingOpen && cloneElement(trackingNavItem, {
                key: `submenu-${trackingNavItem.props.label}`,
                hideIfNoMatch: true,
                dark: true
            })}

            <NavItem
                selected={adminOpen || isAdmin}
                label="Admin"
                Icon={AdminPanelSettings}
                onClick={(event) => {
                    closeSubmenus();
                    setAdminAnchorEl(event.currentTarget);
                }}
            />

            {isAdmin && !adminOpen && Children.map(adminNavItems.props.children,
                (child: ReactElement<ComponentProps<typeof NavItem>>) => (
                    !isValidElement(child) ? child : cloneElement(child, {
                        key: `submenu-${child.props.label}`,
                        hideIfNoMatch: true,
                        dark: true
                    })
                ))}
        </>
    );

    return (
        <nav className={styles.nav}>
            <div className={styles.navGroup}>
                <img
                    alt="premicorr logo"
                    src={logoWhite}
                    className={styles.navLogo}
                />

                <div className={styles.navListTransitionContainer}>
                    <NavList
                        direction="right"
                        in
                    >
                        {mainNavItems}
                    </NavList>
                </div>
            </div>

            <NavGroupPopover
                anchorEl={adminAnchorEl}
                setAnchorEl={setAdminAnchorEl}
            >
                {adminNavItems}
            </NavGroupPopover>

            <NavGroupPopover
                anchorEl={trackingAnchorEl}
                setAnchorEl={setTrackingAnchorEl}
                className={styles.trackingNavGroup}
            >
                {trackingNavItem}
            </NavGroupPopover>
        </nav>
    );
}

interface NavGroupPopoverProps {
    children: ReactNode;
    anchorEl: HTMLElement | null;
    setAnchorEl: Dispatch<SetStateAction<HTMLElement | null >>;
    className?: string;
}

function NavGroupPopover({
    anchorEl, setAnchorEl, children, className
}: NavGroupPopoverProps) {
    return (
        <Popover
            open={!!anchorEl}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{
                horizontal: 'right',
                vertical: 'top'
            }}
            transformOrigin={{
                horizontal: 'left',
                vertical: 'top'
            }}
            classes={{
                paper: clsx(styles.subNavGroup, className)
            }}
        >
            {children}
        </Popover>
    );
}
